import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Snackbar,
	Typography,
} from "@mui/material";
import { Alert } from "@mui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import {
	linkTemplateAttachments,
	unLinkTemplateAttachments,
} from "@/pages/templates/repositories/TemplatesRepositorie";
import type { TemplateAttachment } from "../../../../models/TemplateAttachment";
import { AssetAttachmentTable } from "./asset-Attachments-table";

interface Props {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	TemplateId: number | null;
	TemplateName: string;
	Title: string;
	AssetName: string;
}

export const AssetAttachmentPicker: React.FC<Props> = ({
	show,
	onHide,
	TemplateId,
	TemplateName,
	Title,
	AssetName,
}) => {
	const {
		state: { templateAttachmentsList },
	} = useTemplatesContext();

	const [selectedAttachment, setSelectedAttachment] = useState<
		TemplateAttachment[]
	>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	useEffect(() => {
		if (show) {
			setSelectedAttachment(
				templateAttachmentsList.filter(
					(TemplateAttachment) => TemplateAttachment.IsAssetResource,
				),
			);
		}
	}, [show, templateAttachmentsList]);

	const handleSelectionChanged = (
		newSelectedAttachment: TemplateAttachment[],
	) => {
		setSelectedAttachment(newSelectedAttachment);
	};

	const getNewlyAddedAttachment = (
		currentSelected: TemplateAttachment[],
		originalList: TemplateAttachment[],
	): TemplateAttachment[] => {
		return currentSelected.filter(
			(selectedAttachment) =>
				!originalList.some(
					(existingAttachment) =>
						existingAttachment.UploadFileId ===
							selectedAttachment.UploadFileId &&
						existingAttachment.IsAssetResource,
				),
		);
	};

	const getRemovedAttachment = (
		currentSelected: TemplateAttachment[],
		originalList: TemplateAttachment[],
	): TemplateAttachment[] => {
		return originalList.filter(
			(originalAttachment) =>
				originalAttachment.IsAssetResource &&
				!currentSelected.some(
					(selectedAttachment) =>
						selectedAttachment.UploadFileId === originalAttachment.UploadFileId,
				),
		);
	};

	const handleSave = async () => {
		const newlyAddedAttachments = getNewlyAddedAttachment(
			selectedAttachment,
			templateAttachmentsList,
		);
		const removedAttachments = getRemovedAttachment(
			selectedAttachment,
			templateAttachmentsList,
		);

		if (newlyAddedAttachments.length > 0) {
			const newAssetAttachments = newlyAddedAttachments.map((attachment) => ({
				UploadFileId: attachment.UploadFileId,
				FileName: attachment.FileName,
				IsAssetResource: true,
			}));
			await linkTemplateAttachments(TemplateId as number, newAssetAttachments);
			onHide(true);
		} else onHide(false);

		if (removedAttachments.length > 0) {
			const attachmentsToRemove = removedAttachments.map((attachment) => ({
				UploadFileId: attachment.UploadFileId,
				IsAssetResource: true,
			}));
			await unLinkTemplateAttachments(
				TemplateId as number,
				attachmentsToRemove,
			);
			onHide(true);
		} else onHide(false);
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						ATTACHMENTS FROM ASSET
					</Typography>
					<Typography
						style={{
							display: "flex",
							alignItems: "center",
							fontSize: "1.3rem",
							flexWrap: "wrap",
							gap: "8px",
							padding: "8px 0 0",
						}}
					>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Template:</span>
							<span style={{ marginLeft: "4px" }}>{TemplateName}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Title:</span>
							<span style={{ marginLeft: "4px" }}>{Title}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Asset:</span>
							<span style={{ marginLeft: "4px" }}>{AssetName}</span>
						</div>
					</Typography>
				</DialogTitle>
				<DialogContent>
					<AssetAttachmentTable
						templateAttachment={templateAttachmentsList.filter(
							(TemplateAttachment) => TemplateAttachment.IsAssetResource,
						)}
						onSelectionChanged={handleSelectionChanged}
					/>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px 24px" }}>
				<Button
					variant="contained"
					color="inherit"
					onClick={() => onHide(false)}
				>
					CANCEL
				</Button>
				<Button variant="contained" color="primary" onClick={handleSave}>
					SAVE
				</Button>
			</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
